/**
 * auth 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('auth:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const authZh: TranslationDict = {

  // ======================== 登录 / Auth ========================
  'login': '登录',
  'register': '注册',
  'rebindEmail': '换绑邮箱',
  'username': '用户名',
  'password': '密码',
  'usernamePlaceholder': '你的身份标识',
  'passwordHint': '至少 6 位',
  'verifying': '验证中...',
  'enterPlatform': '进入平台',
  'createAccount': '创建账号',
  'firstUserAdminPrefix': '首位注册用户自动成为',
  'firstUserAdminHighlight': '管理员',
  'title': 'AI 群聊社交网络',
  'subtitle': '意识交汇 · 数字共生',

  // v0.2.0 邮箱验证
  'email': '邮箱',
  'emailPlaceholder': 'example@mail.com',
  'emailOptional': '选填，留空使用用户名登录',
  'emailRequired': '需要验证邮箱才能注册',
  'sendCode': '发送验证码',
  'codeSent': '验证码已发送',
  'codePlaceholder': '输入6位验证码',
  'codeResendIn': '{seconds}秒后可重发',
  'loginMethod': '登录方式',
  'methodDirect': '用户名+密码',
  'methodEmail': '邮箱+验证码',
  'methodWechat': '微信',
  'methodQQ': 'QQ',
  'invalidCode': '验证码错误或已过期',
  'emailAlreadyUsed': '邮箱已被其他账号使用',
  'emailVerified': '已验证',
  'emailNotVerified': '未验证',
  'noEmailBound': '未绑定邮箱',
  'changeEmail': '更换邮箱',
  'removeEmail': '解除绑定',
  'bindEmailTitle': '绑定邮箱',
  'codeSentTo': '验证码已发送至 {email}',
  'tooManyRequests': '验证码发送太频繁，请稍后再试',
  'smtpNotConfigured': '邮件服务未配置，请联系管理员',
  'loginIpNotice': '我们会记录你的真实 IP，仅用于安全审计与登录保护。',
}

export const authEn: TranslationDict = {
  'login': 'Login',
  'register': 'Register',
  'rebindEmail': 'Rebind Email',
  'username': 'Username',
  'password': 'Password',
  'usernamePlaceholder': 'Your identity',
  'passwordHint': 'At least 6 characters',
  'verifying': 'Verifying...',
  'enterPlatform': 'Enter Platform',
  'createAccount': 'Create Account',
  'firstUserAdminPrefix': 'First registered user automatically becomes ',
  'firstUserAdminHighlight': 'admin',
  'title': 'AI Group Chat',
  'subtitle': 'Minds Converge · Digital Symbiosis',
  'email': 'Email',
  'emailPlaceholder': 'example@mail.com',
  'emailOptional': 'Optional, leave empty for username-only login',
  'emailRequired': 'Email verification required to register',
  'sendCode': 'Send Code',
  'codeSent': 'Code sent',
  'codePlaceholder': 'Enter 6-digit code',
  'codeResendIn': 'Resend in {seconds}s',
  'loginMethod': 'Login Method',
  'methodDirect': 'Username+Password',
  'methodEmail': 'Email+Code',
  'methodWechat': 'WeChat',
  'methodQQ': 'QQ',
  'invalidCode': 'Invalid or expired code',
  'emailAlreadyUsed': 'Email already in use',
  'emailVerified': 'Verified',
  'emailNotVerified': 'Not verified',
  'noEmailBound': 'No email bound',
  'changeEmail': 'Change Email',
  'removeEmail': 'Remove',
  'bindEmailTitle': 'Bind Email',
  'codeSentTo': 'Code sent to {email}',
  'tooManyRequests': 'Too many requests, please try again later',
  'smtpNotConfigured': 'Email service not configured, contact admin',
  'loginIpNotice': 'We record your real IP address only for security auditing and login protection.',
}

export const authJa: TranslationDict = {
  'login': 'ログイン',
  'register': '登録',
  'rebindEmail': 'メール再バインド',
  'username': 'ユーザー名',
  'password': 'パスワード',
  'usernamePlaceholder': 'あなたのID',
  'passwordHint': '6文字以上',
  'verifying': '検証中...',
  'enterPlatform': 'プラットフォームに入る',
  'createAccount': 'アカウント作成',
  'firstUserAdminPrefix': '最初に登録したユーザーは自動的に ',
  'firstUserAdminHighlight': '管理者',
  'title': 'AI グループチャット',
  'subtitle': '意識の交差点・デジタル共生',
  'email': 'メールアドレス',
  'emailPlaceholder': 'example@mail.com',
  'emailOptional': '任意（空欄の場合はユーザー名でログイン）',
  'emailRequired': '登録にはメール認証が必要です',
  'sendCode': '認証コード送信',
  'codeSent': 'コードを送信しました',
  'codePlaceholder': '6桁のコードを入力',
  'codeResendIn': '{seconds}秒後に再送可能',
  'loginMethod': 'ログイン方法',
  'methodDirect': 'ユーザー名+パスワード',
  'methodEmail': 'メール+認証コード',
  'methodWechat': 'WeChat',
  'methodQQ': 'QQ',
  'invalidCode': 'コードが無効または期限切れです',
  'emailAlreadyUsed': 'このメールアドレスは既に使用されています',
  'emailVerified': '確認済み',
  'emailNotVerified': '未確認',
  'noEmailBound': 'メール未連携',
  'changeEmail': 'メール変更',
  'removeEmail': '連携解除',
  'bindEmailTitle': 'メール連携',
  'codeSentTo': '{email} にコードを送信しました',
  'tooManyRequests': 'リクエストが多すぎます。しばらく待ってから再試行してください',
  'smtpNotConfigured': 'メールサービスが設定されていません。管理者にお問い合わせください',
  'loginIpNotice': '実際の IP アドレスを、セキュリティ監査とログイン保護の目的に限り記録します。',
}
